/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import {
	findMobileSoftwareBySlug,
	MobileSoftwareType,
} from '@src/configs/software.config'
import { SEO_SITE } from '@src/configs/seo.config'
import { buildBreadcrumbJsonLd, buildSoftwareApplicationJsonLd } from '@utils'

import mobileHeroImg from '@public/images/monolith/mobile-hero-2400.jpg'
import mobileHeroNarrowImg from '@public/images/monolith/mobile-hero-1200.jpg'
import shot01Img from '@public/images/monolith/mobile-s26u-01.png'
import shot02Img from '@public/images/monolith/mobile-s26u-02.png'
import shot03Img from '@public/images/monolith/mobile-s26u-03.png'
import shot04Img from '@public/images/monolith/mobile-s26u-04.png'
import shot05Img from '@public/images/monolith/mobile-s26u-05.png'
import shot06Img from '@public/images/monolith/mobile-s26u-06.png'

import { QRCodeSVG } from 'qrcode.react'

import { useNavigate } from 'react-router-dom'
import NotFoundPage from '@pages/home/NotFound.page'

/** 이 페이지가 다루는 슬러그. 라우트가 고정 경로라 URL 에서 읽지 않는다 */
const SLUG = 'judahs-studio-mobile'

/**
 * On screen — Galaxy S26 Ultra 한 기종의 세로 캡처 6장을 삼성 공식 에뮬레이터 스킨 프레임에 끼운
 * 투명 PNG, 전부 760×1604 (SITEMAP_PLAN §32.10 · §32.14). 크기가 섞이면 행 높이가 들쭉날쭉해진다.
 */
const shotList: { src: string; caption: string }[] = [
	{ src: shot01Img, caption: 'What each footswitch on the unit does.' },
	{ src: shot02Img, caption: 'The banks, songs and setlists the unit holds.' },
	{ src: shot03Img, caption: 'The loop chain and audio routing inside the unit.' },
	{ src: shot04Img, caption: 'Every pedal on the board, and how it connects.' },
	{ src: shot05Img, caption: 'Units nearby, over USB, Bluetooth or Wi-Fi.' },
	{ src: shot06Img, caption: 'Global tempo, clock presets and pages.' },
]

type VisitorType = 'ios' | 'android' | 'desktop'

/**
 * 폰인가, 어느 폰인가. 스토어 링크는 그 폰의 스토어로만 보내고, 그 밖(데스크톱·프리렌더)은 QR 이다.
 * iPadOS 13+ 는 UA 가 Mac 이라 터치 포인트로 가른다 (`SoftwareDetail.page.tsx` 와 같은 길).
 */
const detectVisitor = (): VisitorType => {
	if (typeof window === 'undefined' || (window as any).__JUDAHS_PRERENDER__ === true) {
		return 'desktop'
	}
	const ua = navigator.userAgent
	if (
		/iphone|ipad|ipod/i.test(ua) ||
		(/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
	) {
		return 'ios'
	}
	if (/android/i.test(ua)) {
		return 'android'
	}
	return 'desktop'
}

/**
 * 모바일 앱 상세 — `/software/judahs-studio-mobile` (SITEMAP_PLAN §32 · §32.14).
 *
 * 다운로드 상세(`SoftwareDetail.page.tsx`)와 같은 문법이되, 설치파일 대신 스토어다. 앱은 받는 것이
 * 아니라 스토어에서 여는 것이라 Download 밴드는 셋으로 갈린다 —
 * - 링크가 하나도 없다(지금): `Not yet released.` + 자리만 지키는 idle 행.
 * - 폰: 그 폰의 스토어로 가는 블랙 CTA(이 페이지의 유일한 CTA) + 다른 스토어는 텍스트 행.
 * - 데스크톱: 이 페이지 주소의 QR. 폰으로 찍으면 위의 폰 분기가 열린다. 스토어 배지 그림은 쓰지 않는다 (§32.4).
 */
const MonolithSoftwareMobilePage = () => {
	const navigate = useNavigate()
	const software: MobileSoftwareType | undefined = findMobileSoftwareBySlug(SLUG)

	if (software === undefined) {
		return <NotFoundPage />
	}

	const liveStores = software.stores.filter(aStore => aStore.url !== undefined)
	const pagePath = `/software/${software.slug}`
	const pageUrl = `${SEO_SITE.origin}${pagePath}`
	const [visitor] = React.useState<VisitorType>(detectVisitor)
	/* 폰에서는 자기 스토어가 먼저 */
	const primaryStore = liveStores.find(aStore => aStore.id === visitor)
	const otherStores = liveStores.filter(aStore => aStore.id !== visitor)

	// 구조화 데이터 — 화면에 있는 사실만
	const jsonLd = [
		buildSoftwareApplicationJsonLd({
			name: software.name,
			description: `${software.tagline} ${software.description}`,
			path: pagePath,
			image: new URL(mobileHeroImg, `${SEO_SITE.origin}/`).href,
			operatingSystem: software.requirements
				.filter(aReq => aReq.label === 'iOS' || aReq.label === 'Android')
				.map(aReq => `${aReq.label} ${aReq.value}`)
				.join(', '),
			featureList: [software.lead, ...software.keywords],
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', path: '/' },
			{ name: 'Software', path: '/software' },
			{ name: software.name, path: pagePath },
		]),
	]

	return (
		<View css={style}>
			<Seo
				title={`${software.name} — The editor for Judahs gear, on iOS & Android | Judahs`}
				description={`${software.tagline} ${software.description}`}
				jsonLd={jsonLd}
			/>

			<View className="mnWrap">
				{/* header */}
				<View className="mnPageHeader">
					<View>
						<button
							type="button"
							className="mnBack"
							onClick={() => navigate('/software')}
						>
							← Software
						</button>
						<h1 className="mnPageTitle">{software.name}</h1>
					</View>
					<p className="mnPageLede">{software.tagline}</p>
				</View>

				{/* lead — 기기 라인업 (§32.9). 폰 폭에서는 1200×1200 크롭 */}
				<View className="mnLeadShot">
					<picture>
						<source media="(max-width: 600px)" srcSet={mobileHeroNarrowImg} />
						<img
							src={mobileHeroImg}
							alt={`${software.name} on phones and tablets`}
							className="mnLeadShotImage"
						/>
					</picture>
				</View>

				{/* stores — 미출시 / 폰 / 데스크톱 QR (§32.14) */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">Download</span>
					</View>
					<View className="mnSplitBody">
						{liveStores.length === 0 ? (
							<>
								<p className="mnDownloadLead">{software.unreleasedLead}</p>
								<View className="mnDownloads">
									{software.stores.map(aStore => (
										/* 스토어가 열리기 전의 줄. 자리는 지키되 눌리지 않는다 */
										<View key={aStore.id} className="mnDownload mnDownloadIdle">
											<span className="mnDownloadLabel">{aStore.storeName}</span>
											<span className="mnDownloadExt">{aStore.label}</span>
										</View>
									))}
								</View>
							</>
						) : visitor === 'desktop' ? (
							<View className="mnQr">
								<View className="mnQrCode" aria-label={`QR code for ${pageUrl}`}>
									<QRCodeSVG
										value={pageUrl}
										size={168}
										bgColor="transparent"
										fgColor={mono.ink}
										level="M"
									/>
								</View>
								<View className="mnQrText">
									<p className="mnDownloadLead">{software.qrLead}</p>
									<View className="mnDownloads">
										{liveStores.map(aStore => (
											<a
												key={aStore.id}
												className="mnDownload"
												href={aStore.url}
												target="_blank"
												rel="noreferrer"
											>
												<span className="mnDownloadLabel">{aStore.storeName}</span>
												<span className="mnDownloadExt">{aStore.label}</span>
											</a>
										))}
									</View>
								</View>
							</View>
						) : (
							<>
								{primaryStore !== undefined && (
									<a className="mnCta" href={primaryStore.url} rel="noreferrer">
										Open {primaryStore.storeName}
									</a>
								)}
								{otherStores.length > 0 && (
									<View className="mnDownloads">
										{otherStores.map(aStore => (
											<a
												key={aStore.id}
												className="mnDownload"
												href={aStore.url}
												target="_blank"
												rel="noreferrer"
											>
												<span className="mnDownloadLabel">{aStore.storeName}</span>
												<span className="mnDownloadExt">{aStore.label}</span>
											</a>
										))}
									</View>
								)}
							</>
						)}
					</View>
				</View>

				{/* works with — Judahs 가 만든 전부. 제품명은 워드마크 (PRODUCT_NAMES §0) */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">Works with</span>
					</View>
					<View className="mnSplitBody">
						<ul className="mnWorks">
							{software.worksWith.map(aProduct => {
								const path = `/products/${aProduct.code}`
								return (
									<li key={aProduct.code} className="mnWork">
										<a
											className="mnWorkLink"
											href={path}
											aria-label={aProduct.name}
											onClick={e => {
												e.preventDefault()
												navigate(path)
											}}
										>
											{aProduct.code === 'pls8' ? (
												<KoinosWordmark as="span" />
											) : aProduct.code === 'kdm6' || aProduct.code === 'kdm4' ? (
												<KedemWordmark
													as="span"
													model={aProduct.code.replace('kdm', '')}
												/>
											) : (
												<span className="mnWorkName">{aProduct.name}</span>
											)}
										</a>
									</li>
								)
							})}
						</ul>
					</View>
				</View>

				{/* about — 연결·편집·기록 세 문장, 연결 방법, 낱말 (2026-11-05) */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">What it does</span>
					</View>
					<View className="mnSplitBody">
						<p className="mnDownloadLead">{software.lead}</p>
						<ul className="mnConnections">
							{software.connections.map(aConnection => (
								<li key={aConnection} className="mnConnection">
									{aConnection}
								</li>
							))}
						</ul>
						<ul className="mnKeywords">
							{software.keywords.map(aKeyword => (
								<li key={aKeyword} className="mnKeyword">
									{aKeyword}
								</li>
							))}
						</ul>
					</View>
				</View>

				{/* Studio and Mobile — 같은 에디터, 분담은 표로 */}
				<View className="mnBand mnSplit">
					<View className="mnSplitHead">
						<span className="mnKicker">Studio and Mobile</span>
					</View>
					<View className="mnSplitBody">
						<View className="mnSpecTable">
							{software.studioSplit.map(aRow => (
								<View key={aRow.label} className="mnSpecRow">
									<span className="mnSpecLabel">{aRow.label}</span>
									<span className="mnSpecValue">{aRow.value}</span>
								</View>
							))}
						</View>
						<a
							className="mnTextLink"
							href={software.studioLink.path}
							onClick={e => {
								e.preventDefault()
								navigate(software.studioLink.path)
							}}
						>
							{software.studioLink.label}
						</a>
					</View>
				</View>

				{/* screens — 세로 캡처라 3열 */}
				<View className="mnBand">
					<span className="mnKicker mnSectionKicker">On screen</span>
					<View className="mnShots">
						{shotList.map(aShot => (
							<View key={aShot.caption} className="mnShot">
								<img src={aShot.src} alt="" className="mnShotImage" />
								<div className="mnShotCaption">{aShot.caption}</div>
							</View>
						))}
					</View>
				</View>

				{/* requirements */}
				<View className="mnBand mnSplit mnLast">
					<View className="mnSplitHead">
						<span className="mnKicker">Requirements</span>
					</View>
					<View className="mnSplitBody">
						<View className="mnSpecTable">
							{software.requirements.map(aReq => (
								<View key={aReq.label} className="mnSpecRow">
									<span className="mnSpecLabel">{aReq.label}</span>
									<span className="mnSpecValue">{aReq.value}</span>
								</View>
							))}
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithSoftwareMobilePage

/* 값은 `SoftwareDetail.page.tsx` 와 같다 — 두 상세가 한 문법이어야 한다 */
const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	'.mnPageHeader': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(40px, 5vw, 72px)',

		'.mnBack': {
			display: 'inline-block',
			marginBottom: 28,
			padding: 0,
			border: 0,
			background: 'transparent',
			fontFamily: 'inherit',
			fontSize: 13,
			fontWeight: 500,
			color: mono.faint,
			cursor: 'pointer',
			transition: 'color 200ms ease-out',
			'&:hover': { color: mono.ink },
		},
		'.mnPageTitle': {
			fontSize: 'clamp(40px, 5.5vw, 80px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnLeadShot': {
		marginBottom: 'clamp(48px, 6vw, 90px)',

		'.mnLeadShotImage': {
			width: '100%',
			display: 'block',
			border: `1px solid ${mono.line}`,
			borderRadius: 8,
			boxShadow: '0 30px 60px rgba(20,24,32,0.08)',
		},
	},

	'.mnSplit': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(48px, 5.5vw, 80px) 0',

		'.mnSplitHead': {
			display: 'flex',
			alignItems: 'baseline',
			gap: 20,
		},
		'.mnSplitBody': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 24,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 20,
		},
	},

	'.mnLast': {
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	'.mnDownloadLead': {
		fontSize: 'clamp(24px, 2.6vw, 34px)',
		lineHeight: 1.2,
		fontWeight: 200,
		letterSpacing: '-0.01em',
		color: mono.ink,
		maxWidth: 760,
		textWrap: 'balance',
		marginBottom: 8,
	},

	'.mnDownloads': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		maxWidth: 560,
		borderTop: `1px solid ${mono.line}`,

		'.mnDownload': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			width: '100%',
			padding: '18px 0',
			borderBottom: `1px solid ${mono.line}`,
			color: mono.ink,
			cursor: 'pointer',
			transition: 'padding-left 200ms ease-out',

			'&:hover': {
				paddingLeft: 8,
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 2,
			},

			'&.mnDownloadIdle': {
				color: mono.faint,
				cursor: 'default',
				'&:hover': { paddingLeft: 0 },
			},

			'.mnDownloadLabel': {
				fontSize: 16,
				fontWeight: 400,
			},
			'.mnDownloadExt': {
				fontSize: 12,
				letterSpacing: '.18em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
		},
	},

	/* Works with — 제품명 한 줄, 워드마크는 같은 size (PRODUCT_NAMES §3) */
	'.mnWorks': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'baseline',
		gap: '18px 44px',
		fontSize: 'clamp(28px, 2.8vw, 40px)',
		lineHeight: 1.1,

		'.mnWorkLink': {
			display: 'inline-block',
			color: mono.ink,
			textDecoration: 'none',
			borderBottom: '1px solid transparent',
			transition: 'border-color 200ms ease-out',
			'&:hover': { borderBottomColor: mono.ink },
			'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 4 },
		},
		'.mnWorkName': {
			fontWeight: 300,
			letterSpacing: '-0.01em',
		},
	},

	'.mnTextLink': {
		fontSize: 14,
		fontWeight: 500,
		color: mono.dim,
		textDecoration: 'none',
		borderBottom: `1px solid ${mono.line}`,
		paddingBottom: 2,
		transition: 'color 200ms ease-out, border-color 200ms ease-out',
		'&:hover': { color: mono.ink, borderBottomColor: mono.ink },
	},

	/* 데스크톱 QR — 코드 왼쪽, 글·스토어 행 오른쪽 */
	'.mnQr': {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 40,
		width: '100%',

		'.mnQrCode': {
			flex: '0 0 auto',
			padding: 14,
			border: `1px solid ${mono.line}`,
			borderRadius: 8,
			background: mono.surface,
			lineHeight: 0,
		},
		'.mnQrText': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 24,
			flex: '1 1 auto',
			minWidth: 0,
		},

		'@media (max-width: 600px)': {
			flexDirection: 'column',
		},
	},

	/* 연결 방법 — 큰 낱말 셋, 사이는 hairline */
	'.mnConnections': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'baseline',

		'.mnConnection': {
			fontSize: 'clamp(30px, 3.6vw, 48px)',
			lineHeight: 1.1,
			fontWeight: 200,
			letterSpacing: '-0.01em',
			color: mono.ink,

			'& + .mnConnection': {
				marginLeft: 28,
				paddingLeft: 28,
				borderLeft: `1px solid ${mono.line}`,
			},
		},

		/* 폰 폭에서 셋이 한 줄에 서야 한다 — 줄이 바뀌면 hairline 이 줄 머리에 남는다 */
		'@media (max-width: 600px)': {
			'.mnConnection': {
				fontSize: 25,
				'& + .mnConnection': { marginLeft: 16, paddingLeft: 16 },
			},
		},
	},

	/* 핵심 키워드 — 두 열, 낱말만 */
	'.mnKeywords': {
		listStyle: 'none',
		margin: '8px 0 0',
		padding: 0,
		width: '100%',
		maxWidth: 620,
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: '12px 32px',

		'.mnKeyword': {
			position: 'relative',
			paddingLeft: 20,
			fontSize: 16,
			fontWeight: 300,
			lineHeight: 1.5,
			color: mono.ink,

			'&::before': {
				content: '""',
				position: 'absolute',
				left: 0,
				top: '0.55em',
				width: 5,
				height: 5,
				background: mono.faint,
			},
		},

		'@media (max-width: 600px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnSectionKicker': {
		display: 'block',
		marginBottom: 48,
	},

	/* 세로 캡처는 3열. 기기 프레임이 들어 있는 투명 PNG 라 테두리·radius 를 주지 않는다 */
	'.mnShots': {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: '48px 40px',

		'.mnShot': {
			'.mnShotImage': {
				width: '100%',
				display: 'block',
			},
			'.mnShotCaption': {
				fontSize: 13,
				fontWeight: 300,
				color: mono.faint,
				marginTop: 12,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
		'@media (max-width: 600px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnSpecTable': {
		width: '100%',
		maxWidth: 620,

		'.mnSpecRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 200px) minmax(0, 1fr)',
			gap: 24,
			padding: '15px 0',
			borderBottom: `1px solid ${mono.line}`,
			alignItems: 'baseline',

			'.mnSpecLabel': {
				fontSize: 13,
				letterSpacing: '.14em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
			'.mnSpecValue': {
				fontSize: 16,
				color: mono.ink,
				lineHeight: 1.5,
				fontWeight: 300,
			},
		},
	},
})
